import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  makeStateKey,
  OnDestroy,
  TransferState,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, NavigationStart, Params, Router, RouterLink, Scroll } from '@angular/router';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { MutationResult, QueryRef } from 'apollo-angular';
import { filter, from, map, merge, mergeMap, Observable, of, Subscription, tap } from 'rxjs';
import { CartService } from 'src/app/pages/cart/cart.service';
import { FavouriteLanguage } from 'src/types/graphql';
import { routes } from 'utils/routes';
import { BasePlusComponent } from '../base/base-plus.component';
import { SettingsDialogComponent } from '../../dialogs/settings-dialog/settings-dialog.component';
import { EllipsisTooltipDirective } from '../../../common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { HeaderMenuComponent } from '../header-menu/header-menu.component';
import { IconComponent } from '../../../common/icon/icon.component';
import { NavigationService } from '../../../common/navigation/navigation.service';
import { OpenDialogService } from '../../../common/open-dialog/open-dialog.service';
import { RouteTranslateService } from '../../../common/route-translate/route-translate.service';
import { UserService } from '../../../common/user/user.service';
import { baseUrl, getDefaultModalSettings } from '../../../common/utils/helpers';
import {
  HeaderSearchComponent,
  HeaderSearchTriggerDirective,
  UserSearchKind,
} from './header-search/header-search.component';
import { HeaderSearchService } from './header-search/header-search.service';
import { CartItemsCountGQL, CartItemsCountQuery, CartItemsCountQueryVariables } from './header.query.generated';
import { CartUpdateSubscriptionGQL } from './header.subscription.generated';
import { LOCATION } from '@ng-web-apis/common';
import { StorageService } from '../../../common/storage/storage.service';
import {
  ChangeFavouriteLanguageGQL,
  ChangeFavouriteLanguageMutation,
} from '../../dialogs/language-dialog/language-dialog.mutation.generated';
import { RouteTranslatePipe } from '../../pipes/route-translate.pipe';
import { Capacitor } from '@capacitor/core';
import { ShowOperatorsService } from '../../../pages/search-results/show-operators/show-operators.service';
import { SearchBannerService } from '../../../pages/search-results/list/search-banner/search-banner.service';

@Component({
  selector: 'sharry-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  imports: [
    NgClass,
    AsyncPipe,
    HeaderSearchComponent,
    HeaderSearchTriggerDirective,
    IconComponent,
    I18NextModule,
    EllipsisTooltipDirective,
    MatButtonModule,
    MatMenuModule,
    RouterLink,
    RouteTranslatePipe,
    HeaderMenuComponent,
  ],
})
export class HeaderComponent extends BasePlusComponent implements OnDestroy {
  @ViewChild('search') headerSearch: HeaderSearchComponent;
  @Input() isBehalfCart = false;
  @Input() showBack = false;
  complete = false;
  menuExpanded = false;
  userSearchKind = UserSearchKind;
  types: UserSearchKind[] = [UserSearchKind.FindSearch, UserSearchKind.WhereSearch, UserSearchKind.WhenSearch];
  icons: { [K in UserSearchKind]: string } = {
    find_search: 'target',
    when_search: 'calendar-blank',
    where_search: 'map-pin',
  };
  cartItemsCount$: Observable<number>;
  messagesCount$: Observable<number>;
  notificationsCount$: Observable<number>;
  uuid: string;
  cartId: string;
  cartQuery: QueryRef<CartItemsCountQuery, CartItemsCountQueryVariables>;
  cartUpdateSubscription = new Subscription();
  currentPath$: Observable<{ isMainPath: boolean }>;
  searchValues = {
    find: '',
    where: '',
    when: '',
  };
  langParams: { translateRoute: [string, string]; queryParams: Params; slug: string; lang: FavouriteLanguage };

  constructor(
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    public userService: UserService,
    public dialogRef: MatDialogRef<SettingsDialogComponent>,
    private cd: ChangeDetectorRef,
    private cartUpdateSubscriptionGQL: CartUpdateSubscriptionGQL,
    private cartItemsCountGQL: CartItemsCountGQL,
    private cartService: CartService,
    private navigationService: NavigationService,
    private router: Router,
    private headerSearchService: HeaderSearchService,
    private datePipe: DatePipe,
    private routeTranslateService: RouteTranslateService,
    private openDialogService: OpenDialogService,
    @Inject(LOCATION) private location: Location,
    private transferState: TransferState,
    private storageService: StorageService,
    private changeFavouriteLanguageGQL: ChangeFavouriteLanguageGQL,
    private showOpService: ShowOperatorsService,
    private bannerService: SearchBannerService,
  ) {
    super();
    this.currentPath$ = merge(
      of({ isMainPath: this.isMainPath(this.location.pathname.split('?')[0]) }),
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd || event instanceof NavigationStart || event instanceof Scroll),
        map(event => {
          let url: string;
          if (event instanceof NavigationEnd || event instanceof NavigationStart) {
            url = event.url.split('?')[0];
          } else if (event instanceof Scroll) {
            url = event.routerEvent.url.split('?')[0];
          }
          return { isMainPath: this.isMainPath(url) };
        }),
      ),
    );
    this.cartQuery = this.cartItemsCountGQL.watch({ uuid: null });
    this.cartItemsCount$ = this.cartService.getCart(true, true).pipe(
      tap(cart => {
        this.uuid = cart.uuid;
        this.cartId = cart.id;
      }),
      tap(() => {
        if (this.isPlatformBrowser()) {
          this.cartUpdateSubscription.unsubscribe();
          this.cartUpdateSubscription = this.cartUpdateSubscriptionGQL.subscribe({ id: this.cartId }).subscribe(() => {
            this.cartQuery.refetch({ uuid: this.uuid });
          });
        }
      }),
      mergeMap(() => {
        this.cartQuery.refetch({ uuid: this.uuid });
        return this.cartQuery.valueChanges.pipe(map(r => r.data?.cartByUuid?.cartItemsCount));
      }),
    );
    this.notificationsCount$ = this.userService.unreadNotifications$;
    this.messagesCount$ = this.userService.unreadMessages$;
    this.subscriptions.push(
      this.headerSearchService.queryParams$.subscribe(params => {
        if (params) {
          this.searchValues = {
            find: '',
            where: '',
            when: '',
          };
          if (params.prefix === 'title') {
            this.searchValues.find = params.value;
            if (params.region)
              this.searchValues.where = params.region.includes(', Italia') ? params.region : params.region + ', Italia';
            if (params.province) this.searchValues.where = params.province + ', Italia';
            if (params.city) this.searchValues.where = params.city + ', Italia';
          } else {
            this.searchValues.where = params.value;
          }
          if (!!params.beginDate && !!params.endDate) {
            const start = this.datePipe.transform(params.beginDate, 'dd/MM/yy');
            const end = this.datePipe.transform(params.endDate, 'dd/MM/yy');
            this.searchValues.when = `${start} - ${end}`;
          }
          // this.cd.detectChanges();
        }
      }),
    );
  }

  changeComplete(event: boolean) {
    this.complete = event;
    this.cd.detectChanges();
  }

  onMenuExpanded(isMenuExpanded: boolean) {
    this.menuExpanded = isMenuExpanded;
  }

  onBack() {
    this.navigationService.back();
  }

  isMainPath(url: string) {
    const routeEntries = Object.entries(routes[this.translateService.language as 'it']);
    let replaced = url
      .replace('/it/', '')
      .replace('/en/', '')
      .replace('/es/', '')
      .replace('/fr/', '')
      .replace('/de/', '');

    if (replaced.startsWith('community/')) {
      return false;
    }
    const translateRoute =
      routeEntries.find(([_key, value]) => {
        if (replaced.startsWith('/')) replaced = replaced.substring(1);
        if (replaced === '') replaced = routes[this.translateService.language as 'it']['routes.explore'];
        return replaced === value;
      }) ||
      routeEntries.find(([_key, value]) => {
        if (replaced.startsWith('/')) replaced = replaced.substring(1);
        if (replaced === '') replaced = routes[this.translateService.language as 'it']['routes.explore'];
        return replaced.startsWith(value);
      });
    return [
      'routes.explore',
      'routes.trip',
      'routes.community',
      'routes.profile/home',
      'routes.search-results',
      'routes.map',
      'routes.list',
      'routes.not-found',
      'routes.content-not-found',
    ].includes(translateRoute?.[0] || '');
  }

  clearPlaceholders() {
    this.searchValues = {
      find: '',
      where: '',
      when: '',
    };
    this.headerSearchService.clearQueryParams();
    this.showOpService.set(false);
    this.bannerService.setBanner(null);
    this.cd.detectChanges();
  }

  clearWhere() {
    this.searchValues.where = '';
    this.showOpService.set(false);
    this.bannerService.setBanner(null);
    this.headerSearchService.clearWhereParams();
  }

  clearWhen() {
    this.searchValues.when = '';
    this.showOpService.set(false);
    this.bannerService.setBanner(null);
    this.headerSearchService.clearWhenParams();
  }

  clearFind() {
    this.searchValues.find = '';
    this.showOpService.set(false);
    this.bannerService.setBanner(null);
    this.headerSearchService.clearFindParams();
  }

  onOpenModalSearch() {
    this.headerSearch.onInputClick(UserSearchKind.FindSearch, true);
  }

  onLanguageChange() {
    this.subscriptions.push(
      from(import('../../dialogs/language-dialog/language-dialog.component'))
        .pipe(
          mergeMap(({ LanguageDialogComponent }) =>
            this.openDialogService.openDialog(LanguageDialogComponent, { ...getDefaultModalSettings() }).afterClosed(),
          ),
        )
        .subscribe({
          next: result => {
            if (result?.translateRoute) {
              this.langParams = result;
              const json = JSON.parse(this.transferState.toJson());
              Object.keys(json).forEach(key => {
                if (
                  key.startsWith('SHARRY_CONTENT_WONDER_TRANSFER_STATE_') ||
                  key.startsWith('SHARRY_CONTENT_EVENT_TRANSFER_STATE_') ||
                  key.startsWith('PROFILE_TRANSFER_STATE') ||
                  key.startsWith('SHARRY_CONTENT_SHARRY_TRANSFER_STATE_')
                ) {
                  this.transferState.remove(makeStateKey(key));
                }
              });
              const empty: MutationResult<ChangeFavouriteLanguageMutation> = {
                data: { userChangeFavouriteLanguage: { __typename: 'User', id: null } },
                loading: false,
              };
              this.subscriptions.push(
                (this.userService.user
                  ? this.changeFavouriteLanguageGQL.mutate({
                      input: { favouriteLanguage: result?.lang },
                    })
                  : of(empty)
                )
                  .pipe(
                    mergeMap(() => from(this.storageService.set('sharryFavouriteLanguage', result?.lang))),
                    mergeMap(() => from(this.translateService.changeLanguage(result?.lang))),
                  )
                  .subscribe({
                    next: () => {
                      if (this.userService.user) {
                        this.userService.userSubject.next({
                          ...this.userService.user,
                          favouriteLanguage: result?.lang,
                        });
                        this.userService.user.favouriteLanguage = result?.lang;
                      }
                      const { translateRoute, queryParams, slug } = this.langParams;
                      const navigationPart = [
                        this.routeTranslateService.translateRoute(translateRoute?.[0].replace('routes.', '')),
                      ];

                      if (slug) {
                        navigationPart.push(slug.replace('/', ''));
                      }
                      this.cd.detectChanges();
                      if (Capacitor.getPlatform() !== 'web') {
                        this.router.navigate([this.routeTranslateService.translateRoute('explore')]);
                      } else {
                        this.location.replace(
                          baseUrl +
                            this.router
                              .createUrlTree(navigationPart, {
                                queryParams,
                              })
                              .toString(),
                        );
                      }
                    },
                  }),
              );
            }
          },
        }),
    );
  }

  shouldHavelanguageChange(): boolean {
    const routeEntries = Object.entries(routes[this.translateService.language as 'it']);
    let replaced = this.location.pathname
      .split('?')[0]
      .replace('/it/', '')
      .replace('/en/', '')
      .replace('/es/', '')
      .replace('/fr/', '')
      .replace('/de/', '');
    const translateRoute =
      routeEntries.find(([_key, value]) => {
        if (replaced.startsWith('/')) replaced = replaced.substring(1);
        if (replaced === '') replaced = routes[this.translateService.language as 'it']['routes.explore'];
        return replaced === value;
      }) ||
      routeEntries.find(([_key, value]) => {
        if (replaced.startsWith('/')) replaced = replaced.substring(1);
        if (replaced === '') replaced = routes[this.translateService.language as 'it']['routes.explore'];
        return replaced.startsWith(value);
      });
    return ['routes.explore', 'routes.trip', 'routes.community'].includes(translateRoute?.[0] || '');
  }

  ngOnDestroy(): void {
    this.cartUpdateSubscription.unsubscribe();
  }
}
